import { observer } from "mobx-react-lite";
import { Button, Badge, Space, Flex, message, Popconfirm, Spin, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import { useStore} from "../../stores/root-store-context";
import { Task } from "../../api/TasksApiClient";
import dayjs, { Dayjs } from 'dayjs';
import { 
  CheckOutlined, 
  CloseOutlined, 
  EyeOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from "react-i18next";

const UserTaskPage = observer(() => {
  const { taskStore, applicationStore, eventStore } = useStore();
  
  const [disabledTasks, setDisabledTasks] = useState<{ [key: number]: boolean }>({});
  const { t } = useTranslation();

  useEffect(() => {
    taskStore.fetchTasksByUserId(applicationStore.user.id);
  }, []);

  // Helper function to check if a task is a KanbanTask
  const isKanbanTask = (task: Task): boolean => {
    return task.relatedEntityType === 'KanbanTask' && !!task.relatedEntityId;
  };

  const approveTask = async (taskId: number) => {
    setDisabledTasks(prevState => ({ ...prevState, [taskId]: true }));
    await taskStore.approveTask(taskId);
    message.success(t("Task approved"));
    await taskStore.fetchTasksByUserId(applicationStore.user.id);
    await eventStore.getAll();
    applicationStore.initUserTasks();
    setDisabledTasks(prevState => ({ ...prevState, [taskId]: false }));
  };

  const rejectTask = async (taskId: number) => {
    setDisabledTasks(prevState => ({ ...prevState, [taskId]: true }));
    await taskStore.rejectTask(taskId);
    message.success(t("Task rejected"));
    await taskStore.fetchTasksByUserId(applicationStore.user.id);
    applicationStore.initUserTasks();
    setDisabledTasks(prevState => ({ ...prevState, [taskId]: false }));
  };
  
  // New method for opening KanbanTasks
  const openTask = async (taskId: number) => {
    setDisabledTasks(prevState => ({ ...prevState, [taskId]: true }));
    try {
      const success = await taskStore.openTask(taskId);
      if (success) {
        message.success(t("Opening task..."));
        
        // Find the task and redirect to its link
        const task = taskStore.tasks.find(t => t.id === taskId);
        if (task && task.linkTask) {
          window.location.href = task.linkTask;
        }
      } else {
        message.error(t("Failed to open task"));
      }
    } catch (error) {
      console.error("Error opening task:", error);
      message.error(t("An error occurred"));
    } finally {
      await taskStore.fetchTasksByUserId(applicationStore.user.id);
      applicationStore.initUserTasks();
      setDisabledTasks(prevState => ({ ...prevState, [taskId]: false }));
    }
  };

  // Helper function to determine actions based on task type
  const renderActions = (record: Task) => {
    const isDisabled = disabledTasks[record.id] || record.status === 'Approved' || record.status === 'Rejected' || record.status === 'Done';
    
    // Check if it's a KanbanTask
    if (isKanbanTask(record)) {
      return (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => openTask(record.id)}
          disabled={isDisabled}
        >
          {t("Open Task")}
        </Button>
      );
    } else {
      // Regular task actions (vacation, etc.)
      return (
        <Space>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => approveTask(record.id)}
            disabled={isDisabled}
          >
            {t("Approve")}
          </Button>
          <Button
            type="primary"
            danger
            icon={<CloseOutlined />}
            onClick={() => rejectTask(record.id)}
            disabled={isDisabled}
          >
            {t("Reject")}
          </Button>
        </Space>
      );
    }
  };

  const columns = [
    {
      title: t("Task Title"),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t("Start Date"),
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: t("End Date"),
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: t("Status"),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let statusColor = 'default';
        switch (status) {
          case 'Active': statusColor = 'processing'; break;
          case 'Done': statusColor = 'success'; break;
          case 'Overdue': statusColor = 'error'; break;
          case 'Approved': statusColor = 'success'; break;
          case 'Rejected': statusColor = 'error'; break;
        }
        return <Badge status={statusColor as any} text={t(status)} />;
      },
      filters: [
        { text: t('Active'), value: 'Active' },
        { text: t('Done'), value: 'Done' },
        { text: t('Overdue'), value: 'Overdue' },
        { text: t('Approved'), value: 'Approved' },
        { text: t('Rejected'), value: 'Rejected' }
      ],
      onFilter: (value: any, record: Task) => record.status === value,
    },
    {
      title: t("Type"),
      dataIndex: 'relatedEntityType',
      key: 'type',
      render: (type: string) => {
        if (type === 'KanbanTask') {
          return <Tag color="blue">{t("KanbanTask")}</Tag>;
        }
        return <Tag color="default">{t("Regular")}</Tag>;
      },
      filters: [
        { text: t('KanbanTask'), value: 'KanbanTask' },
        { text: t('Regular'), value: 'regular' }
      ],
      onFilter: (value: any, record: Task) => {
        if (value === 'KanbanTask') {
          return record.relatedEntityType === 'KanbanTask';
        } 
        return record.relatedEntityType !== 'KanbanTask' || !record.relatedEntityType;
      },
    },
    {
      title: t("Actions"),
      dataIndex: 'operation',
      render: (_: any, record: Task) => renderActions(record),
    }
  ];

  const sortedTasks = taskStore.tasks.slice().sort((a, b) => {
    // First by status (Active first, then others)
    if (a.status === 'Active' && b.status !== 'Active') return -1;
    if (a.status !== 'Active' && b.status === 'Active') return 1;
    
    // Then by date (most recent first)
    return dayjs(b.startDate).diff(dayjs(a.startDate));
  });

  return (
    <Spin spinning={taskStore.isLoading}>
      <div className="tasks-font-settings">
      <Table
        rowKey="id"
        columns={columns}
        dataSource={sortedTasks}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
        }}
      />
      </div>
    </Spin>
  );
});

export default UserTaskPage;